import React, {createContext, useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useUserSessionContext} from '../../../../common/components/core/UserSessionContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import Spinner from '../../../../common/components/ui/Spinner';
import {InvoiceOrderDocument} from '../../../../common/types';
import {pathnames as P} from '../../../../common/enums';

export type PaymentContext = {
  invoiceOrder: InvoiceOrderDocument | null,
  link: string,
  updateLink: (link: string) => void,
}

export type PaymentContextProviderProps = {
  children: React.ReactNode,
}

export const defaultContext: PaymentContext = {
  invoiceOrder: null,
  link: '',
  updateLink: () => {}
};

export const PaymentContext = createContext<PaymentContext>(defaultContext);
PaymentContext.displayName = 'PaymentContext';

export const usePaymentContext = () => useContext(PaymentContext);

export const PaymentContextProvider = ({children}: PaymentContextProviderProps) => {
  const params = useParams();
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const E = useMessage();
  const [invoiceOrder, setInvoiceOrder] = useState<InvoiceOrderDocument | null>(null);
  const [link, setLink] = useState<string>('');
  const [loading, setLoading] = useState(true);

  const {paymentPageNotAllowed} = useUserSessionContext();
  const navigate = useNavigate();
  useEffect(() => {
    if (paymentPageNotAllowed) {
      navigate(P.HOME)
    }
  }, []);

  const updateLink = (link: string) => {
    setLink(link);
  };

  const update = async (invoiceOrderID: string) => {
    try {
      const result = await apiService.getInvoiceOrderDetails(invoiceOrderID);
      if (result.errorCode < 0) {
        globalMessage.open(E.serverError);
      }
      if (result.body) {
        setInvoiceOrder(result.body);
      }
    } catch (e) {
      globalMessage.open(E.serverError);
    }
  };

  useEffect(() => {
    if (params.invoiceOrderID) {
      setLoading(true);
      update(params.invoiceOrderID)
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [params.invoiceOrderID]);

  const context: PaymentContext = {
    ...defaultContext,
    invoiceOrder,
    link,
    updateLink,
  };

  return loading
    ? <Spinner/>
    : (
      <PaymentContext.Provider value={context}>
        {children}
      </PaymentContext.Provider>
    );
};
